import React, { useContext, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import API from '../../services/API'
import { GlobalContext, UserContext } from '../../App'
import StaffSelector from '../events/StaffSelector'

import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import EventAvailableIcon from '@mui/icons-material/EventAvailable'
import EventBusyIcon from '@mui/icons-material/EventBusy'
import { alpha, useTheme } from '@mui/material/styles'
import {
  AVAILABILITY_STATUS_COLOURS,
  AVAILABILITY_STATUS_OPTIONS,
  AVAILABILITY_TYPE_LABELS,
  UNAVAILABLE_TYPES,
  describeSubmissionOutcome,
  isAvailableType,
  isSelfApprovedType,
} from '../../helpers/availabilityTypes'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import InputLabel from '@mui/material/InputLabel'
import LinearProgress from '@mui/material/LinearProgress'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import { DataGrid } from '@mui/x-data-grid'
import {
  DatePicker,
  DateTimePicker,
  LocalizationProvider,
} from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'

import ListReportPDF from '../../components/PDF/ListReportPDF'
import PrintButton from '../../components/PDF/PrintButton'
import usePrintChrome from '../../components/PDF/usePrintChrome'
import {
  BAD,
  fmtDate,
  fmtDateTime,
  GOOD,
  MUTED,
  WARN,
} from '../../components/PDF/pdfKit'
import { downloadPdf } from '../../helpers/downloadPdf'

// Availability statuses → PDF pill colours (the grid uses MUI palette names)
const PDF_STATUS_COLORS = {
  Pending: WARN,
  Approved: GOOD,
  Rejected: BAD,
  Cancelled: MUTED,
}

const statusOptions = AVAILABILITY_STATUS_OPTIONS
const statusColor = AVAILABILITY_STATUS_COLOURS

// Two entry points, one form: `kind` decides which fields show. "Available"
// needs only a window; "Unavailable" adds the type, reason and notes.
const buildForm = (kind) => ({
  kind,
  employee: null,
  type: kind === 'available' ? 'Available' : 'Unavailable',
  status: 'Approved',
  start: moment().add(1, 'day').hour(9).minute(0).second(0),
  end: moment().add(1, 'day').hour(17).minute(0).second(0),
  allDay: false,
  reason: '',
  notes: '',
})

const getEmployeeName = (record) => {
  const user = record?.employee?.userAccount
  return `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()
}

// All-day windows are whole days (the server widens them the same way)
const getFormWindow = (form) => ({
  start: form.allDay ? form.start?.clone().startOf('day') : form.start,
  end: form.allDay ? form.end?.clone().endOf('day') : form.end,
})

const isFormWindowValid = (form) => {
  if (!form.start?.isValid?.() || !form.end?.isValid?.()) return false
  return form.allDay
    ? form.end.isSameOrAfter(form.start, 'day')
    : form.end.isAfter(form.start)
}

// "Sat 3 Oct 08:00 – 20:00 · 12h" / "Mon 5 – Wed 7 Oct · 3 days"
const describeFormWindow = (form) => {
  if (!form.start?.isValid?.() || !form.end?.isValid?.()) return 'Choose a start and end'
  if (!isFormWindowValid(form)) return 'The end must be after the start'
  if (form.allDay) {
    const days =
      form.end.clone().startOf('day').diff(form.start.clone().startOf('day'), 'days') + 1
    return days === 1
      ? `${form.start.format('ddd D MMM')} · all day`
      : `${form.start.format('ddd D MMM')} – ${form.end.format('ddd D MMM')} · ${days} days`
  }
  const hours = Math.round(form.end.diff(form.start, 'hours', true) * 10) / 10
  const sameDay = form.start.isSame(form.end, 'day')
  return `${form.start.format('ddd D MMM HH:mm')} – ${form.end.format(
    sameDay ? 'HH:mm' : 'ddd D MMM HH:mm',
  )} · ${hours}h`
}

const Availability = ({ manager = false }) => {
  const theme = useTheme()
  const { user } = useContext(UserContext)
  const { setToast } = useContext(GlobalContext)
  const [records, setRecords] = useState([])
  const [loading, setLoading] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [form, setForm] = useState(() => buildForm('available'))
  const [statusFilter, setStatusFilter] = useState(manager ? 'Pending' : 'All')
  const [leaveBalance, setLeaveBalance] = useState(null)

  const canManage = useMemo(
    () =>
      manager &&
      (user?.employee?.teams?.includes('Personnel') ||
        user?.employee?.teams?.includes('Manager') ||
        user?.security?.isSiteAdmin),
    [manager, user],
  )

  const loadRecords = () => {
    setLoading(true)
    const route = manager ? '/staff-availability' : '/my-availability'
    API.get(route, {
      params: {
        start: moment().subtract(1, 'month').startOf('month').toISOString(),
        end: moment().add(6, 'months').endOf('month').toISOString(),
        status: statusFilter,
      },
    })
      .then((res) => res.status === 200 && setRecords(res.data))
      .finally(() => setLoading(false))
  }

  const loadLeaveBalance = () => {
    if (manager) return
    API.get('/my-leave-balance').then((res) => {
      if (res.status === 200) {
        setLeaveBalance(res.data?.applies === false ? null : res.data)
      }
    })
  }

  useEffect(() => {
    loadRecords()
    loadLeaveBalance()
  }, [manager, statusFilter])

  const openDialog = (kind) => {
    setForm(buildForm(kind))
    setDialogOpen(true)
  }

  const isAvailableForm = form.kind === 'available'

  const saveRecord = () => {
    const route = manager ? '/staff-availability' : '/my-availability'
    const { start, end } = getFormWindow(form)
    const payload = {
      employee: form.employee?._id,
      type: form.type,
      start: start.toISOString(),
      end: end.toISOString(),
      allDay: form.allDay,
      // An available window needs no explanation
      reason: isAvailableForm ? '' : form.reason,
      notes: isAvailableForm ? '' : form.notes,
      status: manager ? form.status : undefined,
    }

    // Validation and overlap refusals (400/409) come back with a message
    // written for the user - show it as-is
    API.post(route, payload).then((res) => {
      if (res.status === 200) {
        setToast({
          message: isAvailableForm
            ? manager
              ? 'Availability added'
              : 'Marked as available'
            : manager
              ? 'Record added'
              : isSelfApprovedType(form.type)
                ? 'Recorded'
                : 'Request submitted',
        })
        setDialogOpen(false)
        loadRecords()
        loadLeaveBalance()
      } else {
        setToast({
          severity: 'error',
          message: res.data?.error || 'Unable to save this record',
        })
      }
    })
  }

  const reviewRecord = (record, status) => {
    API.post(`/staff-availability/${record._id}/review`, { status }).then((res) => {
      if (res.status === 200) {
        setToast({ message: `Request ${status.toLowerCase()}` })
        loadRecords()
      } else {
        setToast({
          severity: 'error',
          message: res.data?.error || 'Unable to review this request',
        })
      }
    })
  }

  const cancelOwnRecord = (record) => {
    API.put(`/my-availability/${record._id}/cancel`).then((res) => {
      if (res.status === 200) {
        setToast({ severity: 'info', message: 'Availability cancelled' })
        loadRecords()
        loadLeaveBalance()
      } else {
        setToast({
          severity: 'error',
          message: res.data?.error || 'Unable to cancel this record',
        })
      }
    })
  }

  const columns = [
    ...(manager
      ? [
          {
            field: 'employeeName',
            headerName: 'Employee',
            minWidth: 220,
            flex: 1,
            valueGetter: (value, row) => getEmployeeName(row),
          },
        ]
      : []),
    {
      field: 'type',
      headerName: 'Type',
      minWidth: 170,
      renderCell: ({ row }) =>
        isAvailableType(row.type) ? (
          <Chip
            size="small"
            color="success"
            variant="outlined"
            icon={<EventAvailableIcon />}
            label={AVAILABILITY_TYPE_LABELS[row.type]}
          />
        ) : (
          <Chip
            size="small"
            variant="outlined"
            icon={<EventBusyIcon />}
            label={AVAILABILITY_TYPE_LABELS[row.type] ?? row.type}
          />
        ),
    },
    {
      field: 'start',
      headerName: 'Start',
      minWidth: 170,
      valueGetter: (value) => moment(value).format('DD MMM YYYY HH:mm'),
    },
    {
      field: 'end',
      headerName: 'End',
      minWidth: 170,
      valueGetter: (value) => moment(value).format('DD MMM YYYY HH:mm'),
    },
    {
      field: 'reason',
      headerName: 'Reason',
      minWidth: 220,
      flex: 1,
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 130,
      renderCell: ({ row }) => (
        <Chip size="small" color={statusColor[row.status] ?? 'default'} label={row.status} />
      ),
    },
    {
      field: 'actions',
      headerName: '',
      minWidth: manager ? 210 : 120,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) =>
        manager && row.status === 'Pending' ? (
          <Stack direction="row" gap={1}>
            <Button
              size="small"
              startIcon={<CheckIcon />}
              color="success"
              onClick={() => reviewRecord(row, 'Approved')}
            >
              Approve
            </Button>
            <Button
              size="small"
              startIcon={<CloseIcon />}
              color="error"
              onClick={() => reviewRecord(row, 'Rejected')}
            >
              Reject
            </Button>
          </Stack>
        ) : !manager && ['Pending', 'Approved'].includes(row.status) ? (
          <Button size="small" color="error" onClick={() => cancelOwnRecord(row)}>
            Cancel
          </Button>
        ) : null,
    },
  ]

  const chrome = usePrintChrome()

  const handlePrint = async () => {
    const title = manager ? 'Staff Availability' : 'My Availability'
    const windowStart = moment().subtract(1, 'month').startOf('month')
    const windowEnd = moment().add(6, 'months').endOf('month')
    const counts = (status) => records.filter((r) => r.status === status).length

    const columns = [
      ...(manager ? [{ label: 'Employee', flex: 1.2 }] : []),
      { label: 'Type', flex: 0.9 },
      { label: 'Start', flex: 0.9 },
      { label: 'End', flex: 0.9 },
      { label: 'Reason', flex: 1.2 },
      { label: 'Notes', flex: 1.2 },
      { label: 'Status', flex: 0.7 },
    ]
    const rows = records.map((record) => [
      ...(manager ? [getEmployeeName(record) || '-'] : []),
      {
        pill: {
          label: AVAILABILITY_TYPE_LABELS[record.type] ?? record.type,
          color: isAvailableType(record.type) ? GOOD : MUTED,
        },
      },
      record.allDay ? `${fmtDate(record.start)} (all day)` : fmtDateTime(record.start),
      record.allDay ? `${fmtDate(record.end)} (all day)` : fmtDateTime(record.end),
      record.reason || '-',
      record.notes || '-',
      {
        pill: {
          label: record.status,
          color: PDF_STATUS_COLORS[record.status] ?? MUTED,
        },
      },
    ])

    await downloadPdf(
      <ListReportPDF
        title={title}
        subtitle={`${windowStart.format('D MMM YYYY')} - ${windowEnd.format(
          'D MMM YYYY',
        )} · Status: ${statusFilter}`}
        docLabel="Availability Report"
        {...chrome}
        stats={[
          { label: 'Records', value: records.length },
          { label: 'Approved', value: counts('Approved'), color: GOOD },
          {
            label: 'Pending',
            value: counts('Pending'),
            color: counts('Pending') ? WARN : undefined,
          },
          {
            label: 'Rejected / Cancelled',
            value: counts('Rejected') + counts('Cancelled'),
          },
        ]}
        columns={columns}
        rows={rows}
        emptyText="No availability records"
      />,
      `AmbuNet_${manager ? 'Staff' : 'My'}_Availability_${moment().format('YYYY-MM-DD')}.pdf`,
    )
  }

  if (manager && !canManage) {
    return <Alert severity="error">You do not have permission to manage availability.</Alert>
  }

  return (
    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="en-gb">
      <Stack gap={2}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2}>
          <Stack>
            <Typography variant="title2">
              {manager ? 'Staff Availability' : 'My Availability'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {manager
                ? 'See when staff are available and review leave, sickness and unavailable periods before assigning shifts.'
                : 'Tell planners when you can work, and submit leave, sickness and unavailable periods.'}
            </Typography>
          </Stack>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            gap={1}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <PrintButton
              onPrint={handlePrint}
              variant="outlined"
              sx={{ whiteSpace: 'nowrap' }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {statusOptions.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* Phones: the two entry buttons share one full-width row */}
            <Stack direction="row" gap={1}>
              <Button
                startIcon={<EventAvailableIcon />}
                variant="contained"
                color="success"
                onClick={() => openDialog('available')}
                sx={{ whiteSpace: 'nowrap', flex: { xs: 1, sm: 'none' } }}
              >
                Available
              </Button>
              <Button
                startIcon={<EventBusyIcon />}
                variant="outlined"
                color="error"
                onClick={() => openDialog('unavailable')}
                sx={{ whiteSpace: 'nowrap', flex: { xs: 1, sm: 'none' } }}
              >
                Unavailable
              </Button>
            </Stack>
          </Stack>
        </Stack>

        {!manager && leaveBalance && (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack gap={2}>
              <Stack>
                <Typography variant="title3">Holiday Balance</Typography>
                <Typography variant="body2" color="text.secondary">
                  Leave year {moment(leaveBalance.leaveYearStart).format('DD MMM YYYY')} -{' '}
                  {moment(leaveBalance.leaveYearEnd).format('DD MMM YYYY')}
                </Typography>
              </Stack>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(2, minmax(0, 1fr))',
                    md: 'repeat(3, minmax(0, 1fr))',
                    lg: 'repeat(6, minmax(0, 1fr))',
                  },
                  gap: 1,
                }}
              >
                {[
                  {
                    label: 'Entitlement',
                    value: `${leaveBalance.proratedEntitlementDays ?? 0} days`,
                  },
                  {
                    label: 'Accrued',
                    value: `${leaveBalance.accruedDays ?? 0} days`,
                  },
                  {
                    label: 'Approved',
                    value: `${leaveBalance.approvedDays ?? 0} days`,
                  },
                  {
                    label: 'Pending',
                    value: `${leaveBalance.pendingDays ?? 0} days`,
                  },
                  {
                    label: 'Remaining',
                    value: `${leaveBalance.remainingDays ?? 0} days`,
                  },
                  {
                    label: 'Projected Remaining',
                    value: `${leaveBalance.projectedRemainingDays ?? 0} days`,
                  },
                ].map((item) => (
                  <Paper
                    key={item.label}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      minHeight: 86,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ lineHeight: 1.25 }}
                    >
                      {item.label}
                    </Typography>
                    <Typography variant="title2" sx={{ lineHeight: 1.15 }}>
                      {item.value}
                    </Typography>
                  </Paper>
                ))}
              </Box>
              <Typography variant="caption" color="text.secondary">
                Accrual is calculated from your employment dates and approved annual leave.
              </Typography>
            </Stack>
          </Paper>
        )}

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          {loading && <LinearProgress />}
          <Box sx={{ height: 560, width: '100%' }}>
            <DataGrid
              rows={records}
              columns={columns}
              getRowId={(row) => row._id}
              disableRowSelectionOnClick
              slots={{
                noRowsOverlay: () => (
                  <Stack alignItems="center" justifyContent="center" height="100%" gap={1}>
                    <EventBusyIcon color="disabled" />
                    <Typography color="text.secondary">No availability records</Typography>
                  </Stack>
                ),
              }}
            />
          </Box>
        </Paper>
      </Stack>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ pb: 1.5 }}>
          <Stack direction="row" gap={1.5} alignItems="center">
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
                bgcolor: alpha(
                  isAvailableForm ? theme.palette.success.main : theme.palette.error.main,
                  0.12,
                ),
                color: isAvailableForm ? 'success.main' : 'error.main',
              }}
            >
              {isAvailableForm ? <EventAvailableIcon /> : <EventBusyIcon />}
            </Box>
            <Box minWidth={0}>
              <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                {isAvailableForm ? 'Available to work' : 'Time off or unavailable'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isAvailableForm
                  ? manager
                    ? 'Record when this person can work'
                    : 'Tell planners when you can work'
                  : manager
                    ? 'Record leave, sickness or an unavailable period'
                    : "Request leave or record when you can't work"}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack gap={2} pt={0.5}>
            {manager && (
              <StaffSelector
                label="Employee"
                value={form.employee}
                onChange={(e, value) => setForm((prev) => ({ ...prev, employee: value }))}
                fullWidth
                noPhoto
              />
            )}
            {!isAvailableForm && (
              <TextField
                select
                label="Type"
                value={form.type}
                onChange={(e) => setForm((prev) => ({ ...prev, type: e.target.value }))}
                fullWidth
              >
                {UNAVAILABLE_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {AVAILABILITY_TYPE_LABELS[type] ?? type}
                  </MenuItem>
                ))}
              </TextField>
            )}
            {manager && !isAvailableForm && !isSelfApprovedType(form.type) && (
              <TextField
                select
                label="Status"
                value={form.status}
                onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
                fullWidth
              >
                {statusOptions
                  .filter((status) => ['Pending', 'Approved'].includes(status))
                  .map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
              </TextField>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
              {form.allDay ? (
                <DatePicker
                  label="From"
                  value={form.start}
                  onChange={(value) => setForm((prev) => ({ ...prev, start: value }))}
                  sx={{ flex: 1 }}
                />
              ) : (
                <DateTimePicker
                  label="Start"
                  value={form.start}
                  onChange={(value) => setForm((prev) => ({ ...prev, start: value }))}
                  sx={{ flex: 1 }}
                />
              )}
              {form.allDay ? (
                <DatePicker
                  label="To"
                  value={form.end}
                  minDate={form.start ?? undefined}
                  onChange={(value) => setForm((prev) => ({ ...prev, end: value }))}
                  sx={{ flex: 1 }}
                />
              ) : (
                <DateTimePicker
                  label="End"
                  value={form.end}
                  minDateTime={form.start ?? undefined}
                  onChange={(value) => setForm((prev) => ({ ...prev, end: value }))}
                  sx={{ flex: 1 }}
                />
              )}
            </Stack>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              gap={1}
              flexWrap="wrap"
            >
              <FormControlLabel
                sx={{ ml: 0 }}
                control={
                  <Switch
                    checked={!!form.allDay}
                    onChange={(e) => setForm((prev) => ({ ...prev, allDay: e.target.checked }))}
                  />
                }
                label="All day"
              />
              <Chip
                size="small"
                variant="outlined"
                color={isFormWindowValid(form) ? (isAvailableForm ? 'success' : 'default') : 'error'}
                label={describeFormWindow(form)}
              />
            </Stack>

            {!isAvailableForm && (
              <>
                <TextField
                  label="Reason"
                  value={form.reason}
                  onChange={(e) => setForm((prev) => ({ ...prev, reason: e.target.value }))}
                  fullWidth
                  placeholder="e.g. Family holiday"
                />
                <TextField
                  label="Notes"
                  value={form.notes}
                  onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                  fullWidth
                  multiline
                  minRows={2}
                  placeholder={manager ? undefined : 'Anything your manager should know'}
                />
              </>
            )}

            {!manager && (
              <Alert
                severity={isAvailableForm ? 'success' : 'info'}
                icon={false}
                sx={{ py: 0.5, '& .MuiAlert-message': { py: 0.5 } }}
              >
                {describeSubmissionOutcome(form.type)}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button color="inherit" onClick={() => setDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={isAvailableForm ? 'success' : 'primary'}
            onClick={saveRecord}
            disabled={(manager && !form.employee) || !isFormWindowValid(form)}
          >
            {isAvailableForm
              ? manager
                ? 'Add availability'
                : 'Mark me available'
              : manager
                ? 'Add record'
                : form.type === 'Sickness'
                  ? 'Record sickness'
                  : 'Submit request'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  )
}

export default Availability
